import React, {useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import '../Games.css';
import {Player} from '../common/types';
import {loadJSON, saveJSON} from '../common/storage';
import {shuffle} from '../common/random';
import PlayerSetup from '../common/PlayerSetup';
import CollectAnswers, {CollectedAnswer} from '../common/CollectAnswers';
import VoteAmongOptions, {VoteOption, VoteRecord} from '../common/VoteAmongOptions';
import {BASE_PROMPTS} from './captionBattlePrompts';

const MIN_PLAYERS = 3;
const PLAYERS_KEY = 'games_players';
const CUSTOM_PROMPTS_KEY = 'captionbattle_custom_prompts';

type Stage = 'setup' | 'collecting' | 'voting' | 'result';

const CaptionBattleGame: React.FC = () => {
    const [players, setPlayers] = useState<Player[]>(() => loadJSON(PLAYERS_KEY, []));
    const [customPrompts, setCustomPrompts] = useState<string[]>(() => loadJSON(CUSTOM_PROMPTS_KEY, []));
    const [newPrompt, setNewPrompt] = useState('');

    const [stage, setStage] = useState<Stage>('setup');
    const [currentPrompt, setCurrentPrompt] = useState('');
    const [usedPrompts, setUsedPrompts] = useState<Set<string>>(new Set());
    const [options, setOptions] = useState<VoteOption[]>([]);
    const [votes, setVotes] = useState<VoteRecord[]>([]);
    const [scores, setScores] = useState<Record<string, number>>({});
    const [roundPoints, setRoundPoints] = useState<Record<string, number>>({});

    const allPrompts = useMemo(() => [...BASE_PROMPTS, ...customPrompts], [customPrompts]);

    const updatePlayers = (next: Player[]) => {
        setPlayers(next);
        saveJSON(PLAYERS_KEY, next);
    };

    const addCustomPrompt = () => {
        const text = newPrompt.trim();
        if (!text) return;
        const next = [...customPrompts, text];
        setCustomPrompts(next);
        saveJSON(CUSTOM_PROMPTS_KEY, next);
        setNewPrompt('');
    };

    const removeCustomPrompt = (text: string) => {
        const next = customPrompts.filter(p => p !== text);
        setCustomPrompts(next);
        saveJSON(CUSTOM_PROMPTS_KEY, next);
    };

    const pickPrompt = (pool: string[], used: Set<string>): {prompt: string; nextUsed: Set<string>} => {
        let available = pool.filter(p => !used.has(p));
        let nextUsed = used;
        if (available.length === 0) {
            available = pool;
            nextUsed = new Set();
        }
        const prompt = available[Math.floor(Math.random() * available.length)];
        const updatedUsed = new Set(nextUsed);
        updatedUsed.add(prompt);
        return {prompt, nextUsed: updatedUsed};
    };

    const beginRound = (pool: string[], used: Set<string>) => {
        const {prompt, nextUsed} = pickPrompt(pool, used);
        setCurrentPrompt(prompt);
        setUsedPrompts(nextUsed);
        setOptions([]);
        setVotes([]);
        setRoundPoints({});
        setStage('collecting');
    };

    const startGame = () => {
        if (players.length < MIN_PLAYERS || allPrompts.length === 0) return;
        setScores(Object.fromEntries(players.map(p => [p.id, 0])));
        beginRound(allPrompts, new Set());
    };

    const nextRound = () => beginRound(allPrompts, usedPrompts);

    const backToSetup = () => {
        setStage('setup');
        setCurrentPrompt('');
    };

    const handleCollectDone = (answers: CollectedAnswer[]) => {
        const opts: VoteOption[] = shuffle(
            answers.map((a): VoteOption => ({id: a.playerId, text: a.text, authorId: a.playerId}))
        );
        setOptions(opts);
        setStage('voting');
    };

    const handleVoteDone = (voteRecords: VoteRecord[]) => {
        setVotes(voteRecords);
        const points: Record<string, number> = {};
        voteRecords.forEach(v => {
            const opt = options.find(o => o.id === v.optionId);
            if (!opt || !opt.authorId) return;
            points[opt.authorId] = (points[opt.authorId] || 0) + 1;
        });
        setRoundPoints(points);
        setScores(s => {
            const next = {...s};
            Object.entries(points).forEach(([pid, pts]) => {
                next[pid] = (next[pid] || 0) + pts;
            });
            return next;
        });
        setStage('result');
    };

    const ranking = useMemo(
        () => [...players].sort((a, b) => (scores[b.id] || 0) - (scores[a.id] || 0)),
        [players, scores]
    );

    return (
        <div className={`GmPage ${stage === 'setup' ? '' : 'GmPage--noBar'}`}>
            <div className="GmContainer">
                <div className="GmTopBar">
                    <Link to="/games" className="GmBack">← Игры</Link>
                    <h1 className="GmTitle" style={{margin: 0}}>Битва подписей</h1>
                </div>

                {stage === 'setup' && (
                    <>
                        <p className="GmSubtitle">
                            Показывается смешной вопрос — каждый втайне пишет свой ответ. Дальше все варианты
                            анонимно перемешиваются, и каждый голосует за самый смешной (кроме своего). Очко —
                            за каждый голос, отданный за твой ответ.
                        </p>

                        <PlayerSetup players={players} setPlayers={updatePlayers} minPlayers={MIN_PLAYERS}/>

                        <div className="GmCard">
                            <h3 className="GmCard-title">Вопросы ({allPrompts.length})</h3>

                            {customPrompts.length > 0 && (
                                <div className="GmList" style={{marginBottom: 12}}>
                                    {customPrompts.map(p => (
                                        <div className="GmListItem" key={p}>
                                            <span className="GmListItem-name">{p}</span>
                                            <button className="GmIconBtn" onClick={() => removeCustomPrompt(p)} aria-label="Удалить">✕</button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="GmInputRow">
                                <input
                                    className="GmInput"
                                    placeholder="Свой вопрос"
                                    value={newPrompt}
                                    onChange={(e) => setNewPrompt(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') addCustomPrompt();
                                    }}
                                />
                                <button className="GmBtn" onClick={addCustomPrompt}>Добавить</button>
                            </div>
                        </div>

                        <div className="GmBottomBar">
                            <div className="GmBottomBar-inner">
                                <button
                                    className="GmBtn GmBtn--primary GmBtn--big"
                                    disabled={players.length < MIN_PLAYERS}
                                    onClick={startGame}
                                >
                                    Начать игру
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {stage === 'collecting' && (
                    <CollectAnswers
                        players={players}
                        placeholder="Твой ответ"
                        prompt={<p className="GmSituationText">{currentPrompt}</p>}
                        onDone={handleCollectDone}
                    />
                )}

                {stage === 'voting' && (
                    <VoteAmongOptions
                        players={players}
                        options={options}
                        voteQuestion="Какой ответ самый смешной?"
                        prompt={<p className="GmSituationText">{currentPrompt}</p>}
                        onDone={handleVoteDone}
                    />
                )}

                {stage === 'result' && (
                    <div className="GmCard">
                        <h3 className="GmCard-title">Итоги</h3>
                        <p className="GmSituationText" style={{marginBottom: 12}}>{currentPrompt}</p>

                        <div className="GmRevealList">
                            {options.map(o => {
                                const voters = votes
                                    .filter(v => v.optionId === o.id)
                                    .map(v => players.find(p => p.id === v.playerId)?.name)
                                    .filter(Boolean);
                                const authorName = players.find(p => p.id === o.authorId)?.name || '?';
                                return (
                                    <div className="GmRevealItem" key={o.id} style={{flexDirection: 'column', alignItems: 'flex-start'}}>
                                        <div><strong>{o.text}</strong> — {authorName}</div>
                                        {voters.length > 0 && <div className="GmHint">Голосовали: {voters.join(', ')}</div>}
                                    </div>
                                );
                            })}
                        </div>

                        <h3 className="GmCard-title" style={{marginTop: 16}}>Счёт</h3>
                        <div className="GmRevealList">
                            {ranking.map(p => (
                                <div className="GmRevealItem" key={p.id}>
                                    {p.name} — {scores[p.id] || 0}{roundPoints[p.id] ? ` (+${roundPoints[p.id]})` : ''}
                                </div>
                            ))}
                        </div>

                        <div className="GmBtnRow" style={{marginTop: 16}}>
                            <button className="GmBtn GmBtn--primary" onClick={nextRound}>Следующий вопрос</button>
                            <button className="GmBtn" onClick={backToSetup}>Закончить игру</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CaptionBattleGame;
